import axios from 'axios';
import { API_URL, toDevice, pollingSubscription } from './adapters';

// "Device" and "Display" are the same entity: the VPS stores it in the
// displays collection, and session handling lives under /api/display-page.

export const getAllDevices = async () => {
  const { data } = await axios.get(`${API_URL}/displays`);
  return data.map(toDevice);
};

export const getDevicesByCompany = async (companyId) => {
  const { data } = await axios.get(`${API_URL}/displays/company/${companyId}`);
  return data.map(toDevice);
};

export const getDevice = async (deviceId, { withTimer = false } = {}) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/displays/${deviceId}`,
      { params: withTimer ? { withTimer: 'true' } : {} }
    );
    return toDevice(data);
  } catch (error) {
    if (error.response?.status === 404) return null;
    throw error;
  }
};

// The row id doubles as the device code, so this resolves the same way as
// getDevice but keeps the calling pages readable.
export const getDeviceByCode = async (deviceCode) => getDevice(deviceCode);

export const createDevice = async (deviceData) => {
  const { data } = await axios.post(`${API_URL}/displays/upsert`, {
    名稱: deviceData.deviceName,
    類別: deviceData.deviceType,
    公司ID: deviceData.companyId,
    登入密碼: deviceData.password
  });
  return toDevice(data.display || data);
};

export const updateDevice = async (deviceId, updates) => {
  const payload = { '🔒 Row ID': deviceId };
  if (updates.deviceName !== undefined) payload.名稱 = updates.deviceName;
  if (updates.deviceType !== undefined) payload.類別 = updates.deviceType;
  if (updates.companyId !== undefined) payload.公司ID = updates.companyId;
  if (updates.password !== undefined) payload.登入密碼 = updates.password;

  const { data } = await axios.post(`${API_URL}/displays/upsert`, payload);
  return toDevice(data.display || data);
};

export const deleteDevice = async (deviceId) => {
  await axios.delete(`${API_URL}/displays/${deviceId}`);
  return true;
};

// ===== Session handling =====

// Resolves to { success, needKickout, device } so DeviceLogin can offer a
// takeover prompt when the display is already claimed elsewhere.
export const deviceLogin = async (deviceCode, sessionId, password) => {
  try {
    const { data } = await axios.post(
      `${API_URL}/display-page/${deviceCode}/login`,
      { sessionId, password }
    );

    if (data.needKickout) {
      return { success: false, needKickout: true, message: data.message };
    }

    return { success: true, device: toDevice(data.display) };
  } catch (error) {
    const res = error.response;
    if (res?.status === 401) {
      return {
        success: false,
        requirePassword: Boolean(res.data?.requirePassword),
        message: res.data?.error || '登入失敗'
      };
    }
    if (res?.status === 404) {
      return { success: false, message: '設備代碼不存在' };
    }
    throw error;
  }
};

export const deviceForceLogin = async (deviceCode, sessionId, password) => {
  try {
    const { data } = await axios.post(
      `${API_URL}/display-page/${deviceCode}/force-login`,
      { sessionId, password }
    );
    return { success: true, device: toDevice(data.display) };
  } catch (error) {
    const res = error.response;
    if (res?.status === 401) {
      return {
        success: false,
        requirePassword: Boolean(res.data?.requirePassword),
        message: res.data?.error || '登入失敗'
      };
    }
    throw error;
  }
};

export const deviceLogout = async (deviceCode, sessionId) => {
  await axios.post(`${API_URL}/display-page/${deviceCode}/logout`, { sessionId });
  return true;
};

export const updateDeviceHeartbeat = async (deviceCode, sessionId, payload = {}) => {
  await axios.post(`${API_URL}/display-page/${deviceCode}/heartbeat`, {
    sessionId,
    ...payload
  });
  return true;
};

export const subscribeToDevice = (deviceId, callback) =>
  pollingSubscription(() => getDevice(deviceId, { withTimer: true }), callback);

export const subscribeToAllDevices = (callback) =>
  pollingSubscription(getAllDevices, callback);
